import type { Asset, AssetId, Concept, ConceptId, LabelAssociation, LabelId } from "@storytell/types";
import { createStore, reconcile } from "solid-js/store";

type RelationshipsCache = {
  concepts: {
    relatedLabels: Record<ConceptId, LabelId[] | undefined>;
  };
  assets: {
    relatedConcepts: Record<AssetId, ConceptId[] | undefined>;
    relatedLabels: Record<AssetId, LabelId[] | undefined>;
  };
  labels: {
    relatedAssets: Record<LabelId, AssetId[] | undefined>;
  };
};

const initialStore = (): RelationshipsCache => ({
  concepts: {
    relatedLabels: {},
  },
  assets: {
    relatedConcepts: {},
    relatedLabels: {},
  },
  labels: {
    relatedAssets: {},
  },
});

const pushCreateUnique = <Id extends string, Related>(
  id: Id,
  record: Record<Id, Related[] | undefined>,
  value: Related
) => {
  const related = record[id];
  if (!related) {
    record[id] = [value];
  } else if (!related.includes(value)) {
    related.push(value);
  }
};

export const buildRelationshipsCache = () => {
  const [store, setStore] = createStore<RelationshipsCache>(initialStore());

  const rebuildFullCache = ({
    assets,
    concepts,
    assetLabelAssociations,
  }: {
    assets: Asset[];
    concepts: Concept[];
    /** When asset list API omits `labels`, merge associations so rels stay correct after refresh-sync. */
    assetLabelAssociations?: readonly Pick<LabelAssociation, "labelId" | "resourceId" | "resourceType">[];
  }) => {
    const assetIdSet = new Set(assets.map((a) => a.id));
    const labelsByAsset = new Map<AssetId, Set<LabelId>>();
    for (const asset of assets) {
      labelsByAsset.set(asset.id, new Set(asset.labels ?? []));
    }
    for (const assoc of assetLabelAssociations ?? []) {
      if (assoc.resourceType !== "asset") continue;
      const aid = assoc.resourceId as AssetId;
      const set = labelsByAsset.get(aid);
      if (set) set.add(assoc.labelId);
      // Asset not loaded yet — still track the label→asset relationship
      // so label detail pages show correct counts and IDs.
      else if (!assetIdSet.has(aid)) labelsByAsset.set(aid, new Set([assoc.labelId]));
    }

    const ns = initialStore();

    for (const [assetId, labels] of labelsByAsset) {
      const uniqueLabels = Array.from(labels);
      ns.assets.relatedLabels[assetId] = uniqueLabels;
      for (const label of uniqueLabels) {
        pushCreateUnique(label, ns.labels.relatedAssets, assetId);
      }
    }
    const newStore = rebuildRelatedLabels(ns, concepts);
    setStore(reconcile(newStore, { merge: true }));
  };

  const rebuildRelatedLabels = (mutStore: RelationshipsCache, concepts: Concept[]) => {
    for (const concept of concepts) {
      const relatedLabels = new Set<LabelId>();
      for (const seed of concept.seeds || []) {
        pushCreateUnique(seed.assetId as AssetId, mutStore.assets.relatedConcepts, concept.id);
        for (const label of mutStore.assets.relatedLabels[seed.assetId as AssetId] || []) {
          relatedLabels.add(label);
        }
      }
      mutStore.concepts.relatedLabels[concept.id] = Array.from(relatedLabels);
    }

    return mutStore;
  };
  const assetLabelDeleted = (assetId: AssetId, labelId: LabelId, concepts: Concept[]) => {
    const newStore = initialStore();
    newStore.assets.relatedLabels = {
      ...store.assets.relatedLabels,
      [assetId]: store.assets.relatedLabels[assetId]?.filter((l) => l !== labelId),
    };
    newStore.labels.relatedAssets = {
      ...store.labels.relatedAssets,
      [labelId]: store.labels.relatedAssets[labelId]?.filter((a) => a !== assetId),
    };
    rebuildRelatedLabels(newStore, concepts);
    setStore(reconcile(newStore, { merge: true }));
  };
  const assetLabelAdded = (assetId: AssetId, labelId: LabelId, concepts: Concept[]) => {
    const newStore = initialStore();
    newStore.assets.relatedLabels = {
      ...store.assets.relatedLabels,
      [assetId]: Array.from(new Set([...(store.assets.relatedLabels[assetId] ?? []), labelId])),
    };
    newStore.labels.relatedAssets = {
      ...store.labels.relatedAssets,
      [labelId]: Array.from(new Set([...(store.labels.relatedAssets[labelId] ?? []), assetId])),
    };
    rebuildRelatedLabels(newStore, concepts);
    setStore(reconcile(newStore, { merge: true }));
  };

  const labelsForConcept = (id: ConceptId) => store.concepts.relatedLabels[id] || [];
  const labelsForAsset = (id: AssetId) => store.assets.relatedLabels[id] || [];
  const conceptsForAsset = (id: AssetId) => store.assets.relatedConcepts[id] || [];
  const assetsForLabel = (id: LabelId) => store.labels.relatedAssets[id] || [];

  const assetHasLabel = (assetId: AssetId, labelId: LabelId) => labelsForAsset(assetId).includes(labelId);
  const conceptHasLabel = (conceptId: ConceptId, labelId: LabelId) => labelsForConcept(conceptId).includes(labelId);

  const assetDeleted = (assetId: AssetId, concepts: Concept[]) => {
    const newStore = initialStore();
    newStore.assets.relatedLabels = {
      ...store.assets.relatedLabels,
    };
    delete newStore.assets.relatedLabels[assetId];
    newStore.labels.relatedAssets = {
      ...store.labels.relatedAssets,
    };
    for (const labelId of Object.keys(newStore.labels.relatedAssets)) {
      newStore.labels.relatedAssets[labelId as LabelId] = newStore.labels.relatedAssets[labelId as LabelId]?.filter(
        (a) => a !== assetId
      );
    }
    rebuildRelatedLabels(newStore, concepts);
    setStore(reconcile(newStore, { merge: true }));
  };

  return {
    rebuildFullCache,
    labelsForConcept,
    conceptsForAsset,
    labelsForAsset,
    assetsForLabel,
    assetLabelDeleted,
    assetLabelAdded,
    assetHasLabel,
    conceptHasLabel,
    assetDeleted,
  };
};
